import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../utils/apiConfig';

export const inr = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
export const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }));
export const pctf = (v) => (v == null ? '—' : `${v}%`);
export const monthLabel = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};
export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : e?.message || 'Something went wrong';
};

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
export const shiftMonth = (m, n) => {
  const [y, mo] = m.split('-').map(Number);
  return ym(new Date(y, mo - 1 + n, 1));
};

export const PRESETS = [
  ['latest', 'Latest month'], ['today', 'Today'], ['yesterday', 'Yesterday'],
  ['this_month', 'This Month'], ['last_month', 'Last Month'], ['last_3', 'Last 3 Months'],
  ['last_6', 'Last 6 Months'], ['last_12', 'Last 12 Months'], ['custom', 'Custom Range'],
];

export function presetRange(p, latest) {
  const now = new Date();
  const cur = ym(now);
  const y = new Date(now); y.setDate(y.getDate() - 1);
  switch (p) {
    case 'latest': return [latest || cur, latest || cur];
    case 'today': case 'this_month': return [cur, cur];
    case 'yesterday': return [ym(y), ym(y)];
    case 'last_month': return [shiftMonth(cur, -1), shiftMonth(cur, -1)];
    case 'last_3': return [shiftMonth(cur, -2), cur];
    case 'last_6': return [shiftMonth(cur, -5), cur];
    case 'last_12': return [shiftMonth(cur, -11), cur];
    default: return null;
  }
}

export const toParams = (f) => {
  const out = {};
  Object.entries(f).forEach(([k, v]) => {
    if (v !== '' && v != null && k !== 'preset') out[k] = v;
  });
  return out;
};

export async function downloadExport(section, params) {
  try {
    const r = await axios.get(`${API}/pos/export/${section}`, { params, responseType: 'blob' });
    const cd = r.headers['content-disposition'] || '';
    const fn = (cd.match(/filename="([^"]+)"/) || [])[1] || `pos_${section}.xlsx`;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = fn; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Export failed');
  }
}

export function usePosGet(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pos/${path}`, { params: JSON.parse(key) });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [path, key]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function usePaged(path, params, defaultSort, extra = {}) {
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const key = JSON.stringify(params) + JSON.stringify(extra);
  useEffect(() => { setPage(1); }, [key, debounced]);
  const merged = { ...params, ...extra, sort, order, page, page_size: 50 };
  if (debounced) merged.q = debounced;
  const res = usePosGet(path, merged);
  const onSort = (k) => {
    if (k === sort) setOrder(order === 'desc' ? 'asc' : 'desc');
    else { setSort(k); setOrder('desc'); }
  };
  return { ...res, sort, order, page, setPage, onSort, search, setSearch };
}
